/**
 * Build the 1-2 letter monogram shown on a factiii avatar that has no image.
 *
 * Takes the first letter of the first word plus the first letter of the second
 * word when one exists — "Climate Change" -> "CC", "Climate" -> "C". Words are
 * split on any whitespace, and leading punctuation/emoji are skipped so a name
 * like "#Climate 🌍Change" still yields "CC".
 *
 * Returns null when the name has no letters or digits at all; callers fall back
 * to the F logo in that case.
 */
export const getFactiiiInitials = (
  name: string | null | undefined
): string | null => {
  if (!name) return null;

  const initials = name
    .trim()
    .split(/\s+/)
    // Take the first alphanumeric character of each word, not simply the first
    // character — factiii names often start with "#" or an emoji.
    .map((word) => word.match(/[\p{L}\p{N}]/u)?.[0] ?? '')
    // Drop wordless tokens BEFORE picking two. A leading standalone emoji is
    // its own whitespace-separated word, so slicing first made "🌍 Earth Watch"
    // yield "E" — the emoji ate one of the two slots.
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();

  return initials || null;
};
